import type { FC } from 'react'
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  isInEmbedVisibilityHideExperimentVariant,
  isInFireworkVisibilityHideExperimentVariant,
} from '@core/abTesting/backend/backendABTesting'
import { ScreenReaderOnly } from '@core/accessibility/screenReader/ScreenReaderOnly'
import { useIsFivePercentControlGroup } from '@core/hooks/isFivePercentControlGroup/isFivePercentControlGroup'
import { useIsPlgControlGroup } from '@core/hooks/useIsPlgControlGroup/useIsPlgControlGroup'
import { useResizeObserver } from '@core/hooks/useResizeObserver'
import { useContextTranslation } from '@core/i18n'
import { logInfo } from '@core/logging'
import { PlayerLayout, VideoPlayerOpenReason } from '@core/player/types'
import { cssUnitToPx } from '@core/styles/tools'
import { WidgetSizeEnum } from '@core/types'
import { useEmbedFeedStore } from '@core/widgets/embedFeed/store'
import { isIframe } from '@core/window'
import { WidgetTitle } from '@embed/components/common/WidgetTitle'
import { AppContextType } from '@embed/components/helpers'
import type { EmbedFeedAppContext } from '@embed/hooks/useEmbedAppContext'
import { GRID_WRAPPER } from '@player/testIds'
import { importVideoPlayer, VideoPlayer } from '@player/VideoPlayer'

import { Container, Wrapper } from './Grid.styles'
import type { Props as GridProps } from './GridComponent'
import {
  columnsByVideoCount,
  columnsForWidth,
  DEFAULT_THUMBNAIL_GAP_PX,
  gridColumnsForWidth,
  RESPONSIVE_THUMBNAIL_WIDTHS,
  responsiveColumnsForWidth,
} from './GridConstants'
import { GridPlaceholders } from './GridPlaceholders'
import { PlaylistVideoPlayerLazy } from './Playlist'

interface Props extends Omit<
  GridProps,
  'appContext' | 'columns' | 'showV2Card' | 'responsive'
> {
  appContext: EmbedFeedAppContext | null
  loading: boolean
}

let gridComponentPromise: Promise<{
  default: FC<GridProps>
}>
const GridComponent = lazy(
  () => gridComponentPromise || import('./GridComponent'),
)

/**
 * Grid component for embed feed.
 */
export const Grid: FC<Props> = (props) => {
  const {
    appContext,
    params,
    videos,
    fetchMoreVideos,
    largeCarousel,
    loading,
  } = props
  const { t } = useContextTranslation()

  const [showV2Card, setShowV2Card] = useState(false)
  const [gridReady, setGridReady] = useState(false)

  const onGridReady = useCallback(() => {
    setGridReady(true)
  }, [])

  useEffect(() => {
    // This is a workaround to make sure GridComponent is loaded asap while also lazy loaded.
    // Component is downloaded while API request resolves. Helps to prevent bundle size
    // increase and long-task blocking.
    gridComponentPromise = import('./GridComponent')
  }, [])

  const { hideVideoPlayer, setVideoPlayerLayout } = useEmbedFeedStore(
    (state) => state.actions,
  )
  const placeholders = useEmbedFeedStore((state) => state.placeholders)
  const player = useEmbedFeedStore((state) => state.player)

  const appContextEmbed = useMemo(
    () =>
      appContext
        ? {
            ...appContext,
            appContextType: AppContextType.EMBED_CONTEXT_TYPE,
            isInIframe: isIframe(),
            pageUrl: params.page_url,
            variant: player.video?.variant,
          }
        : appContext,
    [appContext, player.video, params],
  )

  const [columnWidth, setColumnWidth] = useState(
    `${
      RESPONSIVE_THUMBNAIL_WIDTHS[params.size ?? WidgetSizeEnum.LARGE]
        .maxThumbWidth
    }px`,
  )
  const wrapperRef = useRef<HTMLDivElement>(null)

  const isResponsivePlayerDeck = useMemo(
    () => params.responsive_thumbnails && params.player_deck_layout,
    [params.responsive_thumbnails, params.player_deck_layout],
  )

  const [hasThumbnailSizingCssVars, setHasThumbnailSizingCssVars] =
    useState(false)

  useLayoutEffect(() => {
    if (wrapperRef.current) {
      const style = getComputedStyle(wrapperRef.current)
      const width = style.getPropertyValue('--fw-thumbnail-width')
      const height = style.getPropertyValue('--fw-thumbnail-height')

      // getPropertyValue returns an empty string if the variable is not set
      setHasThumbnailSizingCssVars(width !== '' || height !== '')
    }
  }, [wrapperRef])

  const handleColumns = useCallback(
    (width: number) => {
      if (params.thumbnail_style === 'dynamic') {
        const { columns, showV2Card } = columnsByVideoCount({
          containerWidth: width,
          size: params.size,
          count: videos.length,
        })

        setShowV2Card(showV2Card)
        setColumns(columns)
      } else {
        let columns
        if (isResponsivePlayerDeck && !hasThumbnailSizingCssVars) {
          // get the gap from CSS variable or use default
          const gapStr = wrapperRef.current
            ? getComputedStyle(wrapperRef.current).getPropertyValue(
                '--thumbnail-gap',
              )
            : `${DEFAULT_THUMBNAIL_GAP_PX}px`

          const gap = wrapperRef.current
            ? (cssUnitToPx(gapStr, wrapperRef.current) ??
              DEFAULT_THUMBNAIL_GAP_PX)
            : DEFAULT_THUMBNAIL_GAP_PX

          const responsiveWidth =
            RESPONSIVE_THUMBNAIL_WIDTHS[params.size ?? WidgetSizeEnum.LARGE]

          columns =
            params.mode === 'grid' && params.player_deck_layout
              ? gridColumnsForWidth({
                  minThumbWidth: responsiveWidth.minThumbWidth,
                  maxThumbWidth: responsiveWidth.maxThumbWidth,
                  viewportWidth: width,
                  gap,
                })
              : responsiveColumnsForWidth({
                  maxThumbWidth: responsiveWidth.maxThumbWidth,
                  viewportWidth: width,
                  maxColumns: responsiveWidth.maxColumns,
                  gap,
                })

          setColumnWidth(
            `calc((${width}px - (var(--thumbnail-gap,${DEFAULT_THUMBNAIL_GAP_PX}px) * ${columns - 1})) / ${columns})`,
          )
        } else {
          columns = columnsForWidth(width)
        }

        setColumns(columns)
      }
    },
    [
      params.size,
      params.thumbnail_style,
      params.mode,
      params.player_deck_layout,
      videos.length,
      wrapperRef,
      isResponsivePlayerDeck,
      hasThumbnailSizingCssVars,
    ],
  )

  const gridRef = useResizeObserver<HTMLDivElement>(
    useCallback(
      ([entry]) => {
        const width = entry.target.clientWidth
        handleColumns(width)
      },
      [handleColumns],
    ),
  )
  const [columns, setColumns] = useState(0)

  useLayoutEffect(() => {
    if (gridRef.current) {
      const width = gridRef.current.clientWidth
      handleColumns(width)
    }
    if (isResponsivePlayerDeck && hasThumbnailSizingCssVars) {
      logInfo(
        'responsive_thumbnails is true, but disabled because one of --fw-thumbnail-width or --fw-thumbnail-height is set.',
      )
    }
  }, [
    gridRef,
    handleColumns,
    hasThumbnailSizingCssVars,
    isResponsivePlayerDeck,
  ])

  const onNavigateToVideo = useCallback(
    ({ countOfNextVideos }) => {
      fetchMoreVideos(countOfNextVideos)
    },
    [fetchMoreVideos],
  )

  const onMinimize = useCallback(() => {
    setVideoPlayerLayout(PlayerLayout.MINIMIZED)
  }, [setVideoPlayerLayout])

  const onClose = useCallback(() => {
    hideVideoPlayer()
  }, [hideVideoPlayer])

  const isPlgControlGroup = useIsPlgControlGroup()
  const isFivePercentControlGroup = useIsFivePercentControlGroup()
  const isInInvisibleVariant = isInEmbedVisibilityHideExperimentVariant()
  const isInFireworkVisibilityHideVariant =
    isInFireworkVisibilityHideExperimentVariant()

  if (
    isPlgControlGroup ||
    isFivePercentControlGroup ||
    isInInvisibleVariant ||
    isInFireworkVisibilityHideVariant
  ) {
    return null
  }

  return (
    <Container
      className="fwn-grid"
      ref={gridRef}
      onMouseOver={importVideoPlayer}
      onKeyUp={(e) => {
        /*
         * Prevent keyup events from propagating past our widgets to prevent interference
         * from other javascript on the embedding page causing unexpected behavior.
         */
        e.stopPropagation()
      }}
    >
      {(loading || videos.length > 0) && <WidgetTitle params={params} />}

      <Wrapper
        className={`cy-grid cy-mode-${params.mode} cy-named-${params.name || params.widget_id}`}
        aria-hidden={player.isOpen}
        gridMode={params.mode}
        aria-label={params.title || t('Videos')}
        role="region"
        ref={wrapperRef}
        data-testid={GRID_WRAPPER}
      >
        {params.player_deck_layout ? (
          <ScreenReaderOnly>
            {t(
              'This is a video carousel. Press Tab to move through the visible videos, and use the Previous and Next buttons to reveal more videos. When a video is focused, use the left and right arrow keys to move between videos.',
            )}
          </ScreenReaderOnly>
        ) : params.mode === 'row' ? (
          <ScreenReaderOnly>
            {t(
              'This is a video carousel. Press Tab to move through the visible videos, and use the Previous and Next buttons to reveal more videos.',
            )}
          </ScreenReaderOnly>
        ) : null}
        {(loading || !gridReady) && params.loading_skeleton !== false ? (
          <GridPlaceholders
            columns={columns}
            params={params}
            placeholders={placeholders}
            largeCarousel={largeCarousel}
            responsive={!!isResponsivePlayerDeck && !hasThumbnailSizingCssVars}
            columnWidth={columnWidth}
          />
        ) : null}

        {appContext && !loading ? (
          <Suspense fallback={null}>
            <GridComponent
              {...props}
              appContext={appContext}
              columns={columns}
              columnWidth={columnWidth}
              showV2Card={showV2Card}
              responsive={
                !!isResponsivePlayerDeck && !hasThumbnailSizingCssVars
              }
              onReady={onGridReady}
            />
          </Suspense>
        ) : null}
      </Wrapper>

      {appContextEmbed && player.isOpen && player.video && (
        <VideoPlayer
          appContext={appContextEmbed}
          onClose={onClose}
          params={params}
          onNavigateToVideo={onNavigateToVideo}
          onMinimize={onMinimize}
          videos={videos}
          video={player.video}
          muted={params.muted}
          pip={true}
          playerLayout={player.layout}
          playerOpenReason={VideoPlayerOpenReason.INTERACTION}
        />
      )}

      {appContextEmbed && player.isOpen && player.playlist && (
        <PlaylistVideoPlayerLazy params={params} playlist={player.playlist} />
      )}
    </Container>
  )
}
